/**
 * NASA GIBS WMTS GetCapabilities helper service
 * Fetches and parses WMTS capabilities to get correct parameters
 */

import { logger } from '../../utils/logger.js'

const log = logger.create('wmts')

export interface WMTSLayer {
  identifier: string
  title: string
  abstract?: string | undefined
  formats: string[]
  tileMatrixSets: string[]
  dimensions?: WMTSDimension[] | undefined
  resourceUrls?: WMTSResourceUrl[] | undefined
}

export interface WMTSDimension {
  identifier: string
  default: string
  values: string[]
}

export interface WMTSResourceUrl {
  format: string
  resourceType: string
  template: string
}

export interface WMTSTileMatrix {
  identifier: string | undefined
  scaleDenominator: number
  topLeftCorner: number[] | undefined
  tileWidth: number
  tileHeight: number
  matrixWidth: number
  matrixHeight: number
}

export interface WMTSTileMatrixSet {
  identifier: string | undefined
  supportedCRS: string | undefined
  tileMatrices: WMTSTileMatrix[]
}

export interface WMTSCapabilities {
  version: string
  layers: WMTSLayer[]
  tileMatrixSets: WMTSTileMatrixSet[]
  serviceMetadata: {
    title: string
    abstract: string
    keywords: string[]
  }
}

export class GibsCapabilitiesService {
  private static readonly GIBS_WMTS_CAPABILITIES =
    'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/1.0.0/WMTSCapabilities.xml'
  private static readonly GIBS_WMS_CAPABILITIES =
    'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi?SERVICE=WMS&REQUEST=GetCapabilities'

  // Cache WMTS capabilities for 10 minutes
  private static wmtsCapabilitiesCache: { data: WMTSCapabilities; fetchedAt: number } | null = null
  private static readonly WMTS_CACHE_TTL_MS = 10 * 60 * 1000

  /**
   * Fetch and parse NASA GIBS WMTS capabilities (cached for 10 min)
   */
  static async getCapabilities(): Promise<WMTSCapabilities> {
    const cached = this.wmtsCapabilitiesCache
    if (cached && Date.now() - cached.fetchedAt < this.WMTS_CACHE_TTL_MS) {
      return cached.data
    }

    try {
      log.info('Fetching NASA GIBS WMTS capabilities...')

      const response = await fetch(this.GIBS_WMTS_CAPABILITIES, {
        mode: 'cors',
        cache: 'default',
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const xmlText = await response.text()
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml')

      // Check for XML parsing errors
      const parserError = xmlDoc.querySelector('parsererror')
      if (parserError) {
        throw new Error(`XML parsing error: ${parserError.textContent}`)
      }

      const data = this.parseCapabilities(xmlDoc)
      this.wmtsCapabilitiesCache = { data, fetchedAt: Date.now() }
      return data
    } catch (error) {
      log.error('Failed to fetch GIBS capabilities:', error)
      throw error
    }
  }

  /**
   * Parse WMTS capabilities XML document
   */
  private static parseCapabilities(xmlDoc: Document): WMTSCapabilities {
    const layers: WMTSLayer[] = []

    // Parse service metadata
    const serviceId = xmlDoc.querySelector('ServiceIdentification')
    const serviceMetadata = {
      title: serviceId?.querySelector('Title')?.textContent ?? 'NASA GIBS',
      abstract: serviceId?.querySelector('Abstract')?.textContent ?? '',
      keywords: Array.from(serviceId?.querySelectorAll('Keywords Keyword') ?? []).map(
        k => k.textContent
      ),
    }

    // Parse layers
    const layerElements = xmlDoc.querySelectorAll('Layer')
    layerElements.forEach(layerEl => {
      const identifier = layerEl.querySelector('Identifier')?.textContent
      if (!identifier) {
        return
      }

      const layer: WMTSLayer = {
        identifier,
        title: layerEl.querySelector('Title')?.textContent ?? identifier,
        abstract: layerEl.querySelector('Abstract')?.textContent,
        formats: Array.from(layerEl.querySelectorAll('Format')).map(f => f.textContent),
        tileMatrixSets: Array.from(layerEl.querySelectorAll('TileMatrixSetLink TileMatrixSet')).map(
          tms => tms.textContent
        ),
        dimensions: this.parseDimensions(layerEl),
        resourceUrls: this.parseResourceUrls(layerEl),
      }

      layers.push(layer)
    })

    // Parse tile matrix sets
    const tileMatrixSets = Array.from(xmlDoc.querySelectorAll('TileMatrixSet')).map(tms =>
      this.parseTileMatrixSet(tms)
    )

    return {
      version: xmlDoc.querySelector('Capabilities')?.getAttribute('version') ?? '1.0.0',
      layers,
      tileMatrixSets,
      serviceMetadata,
    }
  }

  /**
   * Parse layer dimensions (like TIME)
   */
  private static parseDimensions(layerEl: Element): WMTSDimension[] {
    const dimensions: WMTSDimension[] = []

    const dimensionElements = layerEl.querySelectorAll('Dimension')
    dimensionElements.forEach(dimEl => {
      const identifier = dimEl.querySelector('Identifier')?.textContent
      if (!identifier) {
        return
      }

      const defaultValue = dimEl.querySelector('Default')?.textContent ?? ''
      const values = Array.from(dimEl.querySelectorAll('Value')).map(v => v.textContent)

      dimensions.push({
        identifier,
        default: defaultValue,
        values,
      })
    })

    return dimensions
  }

  /**
   * Parse resource URLs for REST-style requests
   */
  private static parseResourceUrls(layerEl: Element): WMTSResourceUrl[] {
    const resourceUrls: WMTSResourceUrl[] = []

    const urlElements = layerEl.querySelectorAll('ResourceURL')
    urlElements.forEach(urlEl => {
      const format = urlEl.getAttribute('format') ?? ''
      const resourceType = urlEl.getAttribute('resourceType') ?? ''
      const template = urlEl.getAttribute('template') ?? ''

      if (template) {
        resourceUrls.push({
          format,
          resourceType,
          template,
        })
      }
    })

    return resourceUrls
  }

  /**
   * Parse tile matrix set information
   */
  private static parseTileMatrixSet(tmsEl: Element): WMTSTileMatrixSet {
    const identifier = tmsEl.querySelector('Identifier')?.textContent
    const supportedCRS = tmsEl.querySelector('SupportedCRS')?.textContent

    const tileMatrices = Array.from(tmsEl.querySelectorAll('TileMatrix')).map(tmEl => ({
      identifier: tmEl.querySelector('Identifier')?.textContent,
      scaleDenominator: parseFloat(tmEl.querySelector('ScaleDenominator')?.textContent ?? '0'),
      topLeftCorner: tmEl.querySelector('TopLeftCorner')?.textContent.split(' ').map(Number),
      tileWidth: parseInt(tmEl.querySelector('TileWidth')?.textContent ?? '256'),
      tileHeight: parseInt(tmEl.querySelector('TileHeight')?.textContent ?? '256'),
      matrixWidth: parseInt(tmEl.querySelector('MatrixWidth')?.textContent ?? '1'),
      matrixHeight: parseInt(tmEl.querySelector('MatrixHeight')?.textContent ?? '1'),
    }))

    return {
      identifier,
      supportedCRS,
      tileMatrices,
    }
  }

  /**
   * Find a specific layer by identifier
   */
  static async getLayer(layerIdentifier: string): Promise<WMTSLayer | null> {
    const capabilities = await this.getCapabilities()
    return capabilities.layers.find(layer => layer.identifier === layerIdentifier) ?? null
  }

  /**
   * Fetch and parse NASA GIBS WMS capabilities for comprehensive layer list
   */
  static async getWMSCapabilities(): Promise<WMTSLayer[]> {
    try {
      log.info('🌍 Fetching NASA GIBS WMS capabilities...')

      const response = await fetch(this.GIBS_WMS_CAPABILITIES, {
        mode: 'cors',
        cache: 'default',
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const xmlText = await response.text()
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml')

      // Check for XML parsing errors
      const parserError = xmlDoc.querySelector('parsererror')
      if (parserError) {
        throw new Error(`XML parsing error: ${parserError.textContent}`)
      }

      return this.parseWMSLayers(xmlDoc)
    } catch (error) {
      log.error('❌ Failed to fetch WMS capabilities:', error)
      throw error
    }
  }

  /**
   * Parse WMS layers and convert to WMTS format
   */
  private static parseWMSLayers(xmlDoc: Document): WMTSLayer[] {
    const layers: WMTSLayer[] = []

    const layerElements = xmlDoc.querySelectorAll('Layer[queryable]')
    layerElements.forEach(layerEl => {
      const name = layerEl.querySelector('Name')?.textContent
      if (!name) {
        return
      }

      const layer: WMTSLayer = {
        identifier: name,
        title: layerEl.querySelector('Title')?.textContent ?? name,
        abstract: layerEl.querySelector('Abstract')?.textContent,
        formats: ['image/png'], // Default for GIBS
        tileMatrixSets: ['GoogleMapsCompatible_Level9'], // Default for GIBS EPSG:3857
        dimensions: this.parseWMSDimensions(layerEl),
      }

      layers.push(layer)
    })

    return layers
  }

  /**
   * Parse WMS dimensions
   */
  private static parseWMSDimensions(layerEl: Element): WMTSDimension[] {
    const dimensions: WMTSDimension[] = []

    const dimensionElements = layerEl.querySelectorAll('Dimension')
    dimensionElements.forEach(dimEl => {
      const name = dimEl.getAttribute('name')
      if (!name) {
        return
      }

      const defaultValue = dimEl.getAttribute('default') ?? ''
      const values = dimEl.textContent.split(',')

      dimensions.push({
        identifier: name.toUpperCase(), // WMS uses lowercase, WMTS uses uppercase
        default: defaultValue,
        values,
      })
    })

    return dimensions
  }

  /**
   * Get filtered relevant layers for outdoor/hiking use
   */
  static async getRelevantLayers(): Promise<WMTSLayer[]> {
    const layers = await this.getWMSCapabilities()

    // Filter for layers most relevant to outdoor/hiking activities
    const relevantKeywords = [
      'snow',
      'cloud',
      'vegetation',
      'fire',
      'temperature',
      'precipitation',
      'ndvi',
      'modis',
      'viirs',
      'landsat',
      'weather',
      'storm',
      'ice',
      'aerosol',
      'air_quality',
      'lst', // Land Surface Temperature
    ]

    return layers
      .filter(layer => {
        const searchText = (
          layer.identifier +
          ' ' +
          layer.title +
          ' ' +
          (layer.abstract ?? '')
        ).toLowerCase()
        return relevantKeywords.some(keyword => searchText.includes(keyword))
      })
      .slice(0, 20) // Limit to most relevant 20 layers
  }

  /**
   * Get all available snow-related layers
   */
  static async getSnowLayers(): Promise<WMTSLayer[]> {
    const capabilities = await this.getCapabilities()
    return capabilities.layers.filter(
      layer =>
        layer.identifier.toLowerCase().includes('snow') ||
        layer.title.toLowerCase().includes('snow')
    )
  }

  /**
   * Generate proper WMTS URL from layer information
   */
  static buildWMTSUrl(
    layer: WMTSLayer,
    options: {
      format?: string
      tileMatrixSet?: string
      time?: string
    } = {}
  ): string {
    const format = options.format ?? 'image/png'
    const tileMatrixSet = options.tileMatrixSet ?? 'GoogleMapsCompatible_Level9'
    const time = options.time ?? new Date().toISOString().split('T')[0] // Default to today

    // Use REST-style URL if available
    const restUrl = layer.resourceUrls?.find(
      url => url.resourceType === 'tile' && url.format === format
    )

    if (restUrl) {
      return restUrl.template
        .replace('{TileMatrix}', '{z}')
        .replace('{TileRow}', '{y}')
        .replace('{TileCol}', '{x}')
        .replace('{Time}', time)
    }

    // Fallback to KVP-style URL
    return `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/wmts.cgi?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer.identifier}&STYLE=default&TILEMATRIXSET=${tileMatrixSet}&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&TIME=${time}&FORMAT=${format.replace('/', '%2F')}`
  }

  // Cache for CMR nearest-date lookups: "collection/requestedDate" → resolved date
  private static cmrDateCache = new Map<string, string>()
  private static readonly CMR_CACHE_MAX = 100

  /**
   * Get the available time range for a GIBS layer from WMTS capabilities.
   * Returns the earliest and latest available times, plus the default (most recent) time.
   * Cached via getCapabilities().
   */
  static async getLayerTimeRange(
    layerIdentifier: string
  ): Promise<{ earliest: string; latest: string; default: string } | null> {
    try {
      const capabilities = await this.getCapabilities()
      const layer = capabilities.layers.find(l => l.identifier === layerIdentifier)
      if (!layer) {
        return null
      }

      const timeDim = layer.dimensions?.find(d => d.identifier.toUpperCase() === 'TIME')
      if (!timeDim) {
        return null
      }

      // The default value is the most recent available time
      const defaultTime = timeDim.default

      // Parse time ranges to find earliest/latest bounds
      let earliest = ''
      let latest = ''
      for (const value of timeDim.values) {
        if (value.includes('/')) {
          const parts = value.split('/')
          if (!earliest || parts[0] < earliest) {
            earliest = parts[0]
          }
          if (parts.length >= 2 && (!latest || parts[1] > latest)) {
            latest = parts[1]
          }
        } else {
          if (!earliest || value < earliest) {
            earliest = value
          }
          if (!latest || value > latest) {
            latest = value
          }
        }
      }

      return {
        earliest: earliest || defaultTime,
        latest: latest || defaultTime,
        default: defaultTime,
      }
    } catch (error) {
      log.warn(`Failed to get time range for ${layerIdentifier}:`, error)
      return null
    }
  }

  private static subdailyTimesCache = new Map<string, { times: string[]; fetchedAt: number }>()

  /**
   * Get sorted list of available subdaily times for a layer.
   * Expands range entries (start/end/period) into discrete timestamps.
   */
  static async getAvailableSubdailyTimes(layerIdentifier: string): Promise<string[]> {
    const cached = this.subdailyTimesCache.get(layerIdentifier)
    if (cached && Date.now() - cached.fetchedAt < this.WMTS_CACHE_TTL_MS) {
      return cached.times
    }

    const capabilities = await this.getCapabilities()
    const layer = capabilities.layers.find(l => l.identifier === layerIdentifier)
    if (!layer) return []

    const timeDim = layer.dimensions?.find(d => d.identifier.toUpperCase() === 'TIME')
    if (!timeDim) return []

    const timeSet = new Set<string>()
    for (const value of timeDim.values) {
      if (!value.includes('T')) continue
      if (!value.includes('/')) {
        timeSet.add(value)
        continue
      }
      const parts = value.split('/')
      if (parts.length < 3) continue
      const start = new Date(parts[0])
      const end = new Date(parts[1])
      const periodMs = parseIsoDuration(parts[2])
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || periodMs <= 0) continue
      for (let t = start.getTime(); t <= end.getTime(); t += periodMs) {
        timeSet.add(new Date(t).toISOString().replace('.000Z', 'Z'))
      }
    }

    const times = Array.from(timeSet).sort()
    this.subdailyTimesCache.set(layerIdentifier, { times, fetchedAt: Date.now() })
    return times
  }

  /**
   * Clamp a requested time to the available range for a GIBS layer.
   * For subdaily layers, finds the nearest available time from capabilities.
   * Falls back to date-only format on error (always valid for GIBS).
   */
  static async clampToAvailableTime(
    layerIdentifier: string,
    requestedTime: Date,
    subdaily: boolean
  ): Promise<string> {
    const range = await this.getLayerTimeRange(layerIdentifier)
    if (!range) {
      return requestedTime.toISOString().split('T')[0]
    }

    if (!subdaily) {
      const requestedDate = requestedTime.toISOString().split('T')[0]
      const earliestDate = range.earliest.split('T')[0]
      const latestDate = range.latest.split('T')[0]
      if (requestedDate < earliestDate) return earliestDate
      if (requestedDate > latestDate) return latestDate
      return requestedDate
    }

    try {
      const times = await this.getAvailableSubdailyTimes(layerIdentifier)
      if (times.length === 0) {
        return requestedTime.toISOString().split('T')[0]
      }

      const requestedIso = requestedTime.toISOString().replace('.000Z', 'Z')
      const nearest = findNearest(times, requestedIso)
      return nearest
    } catch {
      return requestedTime.toISOString().split('T')[0]
    }
  }

  /**
   * Query NASA CMR to find the most recent date with data on or before the requested date.
   * Used for sparse-coverage layers like HLS Sentinel-2 and Landsat.
   *
   * @param cmrShortName  CMR collection short name (e.g. "HLSS30", "HLSL30")
   * @param requestedDate YYYY-MM-DD date string
   * @returns The nearest available date (YYYY-MM-DD), or the requested date if CMR query fails
   */
  static async getNearestAvailableDate(
    cmrShortName: string,
    requestedDate: string
  ): Promise<string> {
    const cacheKey = `${cmrShortName}/${requestedDate}`
    const cached = this.cmrDateCache.get(cacheKey)
    if (cached) {
      return cached
    }

    try {
      const url =
        `https://cmr.earthdata.nasa.gov/search/granules.json` +
        `?short_name=${cmrShortName}` +
        `&temporal[]=,${requestedDate}T23:59:59Z` +
        `&sort_key[]=-start_date` +
        `&page_size=1`

      const response = await fetch(url, { signal: AbortSignal.timeout(5000) })
      if (!response.ok) {
        throw new Error(`CMR ${response.status}`)
      }

      const data = await response.json()
      const entries = data?.feed?.entry
      if (entries && entries.length > 0) {
        // time_start is ISO: "2026-03-20T00:00:00.000Z"
        const resolved = entries[0].time_start?.split('T')[0]
        if (resolved) {
          // Cache and evict oldest if full
          if (this.cmrDateCache.size >= this.CMR_CACHE_MAX) {
            const firstKey = this.cmrDateCache.keys().next().value!
            this.cmrDateCache.delete(firstKey)
          }
          this.cmrDateCache.set(cacheKey, resolved)
          return resolved
        }
      }
    } catch (error) {
      log.warn(`CMR nearest-date lookup failed for ${cmrShortName}:`, error)
    }

    return requestedDate
  }
}

function parseIsoDuration(iso: string): number {
  const match = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/)
  if (!match) return 0
  const hours = parseInt(match[1] || '0', 10)
  const minutes = parseInt(match[2] || '0', 10)
  const seconds = parseFloat(match[3] || '0')
  return (hours * 3600 + minutes * 60 + seconds) * 1000
}

function findNearest(sortedTimes: string[], target: string): string {
  let lo = 0
  let hi = sortedTimes.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (sortedTimes[mid] < target) lo = mid + 1
    else hi = mid
  }
  if (lo === 0) return sortedTimes[0]
  if (lo >= sortedTimes.length) return sortedTimes[sortedTimes.length - 1]
  const before = sortedTimes[lo - 1]
  const after = sortedTimes[lo]
  const targetMs = new Date(target).getTime()
  return targetMs - new Date(before).getTime() <= new Date(after).getTime() - targetMs
    ? before
    : after
}
